(function () {
  'use strict';
  function textBody(review) {
    const raw = review.body || review.body_html || '';
    if (typeof document !== 'undefined') {
      const box = document.createElement('div');
      box.innerHTML = raw;
      return box.textContent.trim();
    }
    return raw.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"').trim();
  }
  const normalize = value => String(value).replace(/\s+/g, ' ').trim().toLowerCase();
  function correctReviews(reviews, productId, rules) {
    const mapped = new Map(rules.reviews.map(r => [normalize(r.body) + '|' + r.date, r]));
    const result = new Map();
    for (const review of reviews) {
      const body = textBody(review);
      const key = normalize(body) + '|' + String(review.created_at).slice(0, 10);
      const match = mapped.get(key);
      const reviewer = normalize(review.reviewer_name || '');
      const sourceName = match && normalize(match.reviewer_name);
      const source = match && (reviewer === sourceName || reviewer.startsWith(sourceName + ' ') || /^etsy (customer|buyer|user)$/.test(reviewer)) ? match : null;
      if (source && String(source.product_id) !== String(productId)) continue;
      const identity = source ? 'import:' + key : 'judge:' + review.uuid;
      const candidate = { ...review, body, imported_from_etsy: Boolean(source) || (review.transparency_badges || []).includes('review_written_in_etsy') };
      const previous = result.get(identity);
      const priority = r => source && normalize(r.reviewer_name) === normalize(source.reviewer_name) ? 2 : r.verified_buyer ? 1 : 0;
      if (!previous || priority(candidate) > priority(previous)) result.set(identity, candidate);
    }
    return [...result.values()].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { correctReviews, textBody };
    return;
  }
  if (window.CgReviewSync) { window.CgReviewSync.scan(); return; }
  const requests = new Map();
  let rulesPromise;
  function loadRules(url) {
    return rulesPromise ||= fetch(url).then(r => { if (!r.ok) throw new Error('Review mapping unavailable'); return r.json(); });
  }
  function loadReviews(productId, shop) {
    const key = shop + ':' + productId;
    if (!requests.has(key)) requests.set(key, (async () => {
      const reviews = [];
      let page = 1, pages = 1;
      do {
        const url = new URL('https://cdn.judge.me/reviews/reviews_for_widget');
        url.search = new URLSearchParams({ shop_domain: shop, platform: 'shopify', product_id: productId, per_page: '30', page: String(page), sort_by: 'created_at', sort_dir: 'desc' });
        const response = await fetch(url, { signal: AbortSignal.timeout(12000) });
        if (!response.ok) throw new Error('Reviews unavailable');
        const data = await response.json();
        if (!Array.isArray(data.reviews) || !data.pagination) throw new Error('Invalid review response');
        reviews.push(...data.reviews);
        pages = Number(data.pagination.total_pages) || 1;
        page += 1;
      } while (page <= pages);
      return reviews;
    })());
    return requests.get(key);
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function stars(rating, template) {
    const node = template.cloneNode(true);
    node.classList.remove('cg-stars--large');
    node.style.setProperty('--cg-stars-fill', Math.max(0, Math.min(100, Number(rating) * 20)) + '%');
    node.setAttribute('aria-label', Number(rating).toFixed(1) + ' out of 5 stars');
    return node;
  }
  function cards(reviews, template, title) {
    const list = element('ul', 'cg-review-cards');
    list.setAttribute('aria-label', 'Reviews of the ' + title);
    for (const review of reviews) {
      if (!review.body || normalize(review.body) === normalize(review.product_title) || normalize(review.body) === normalize(title)) continue;
      const item = element('li', 'cg-review-card');
      const figure = element('figure', 'cg-review-card__figure');
      const meta = element('div', 'cg-review-card__meta');
      meta.append(stars(review.rating, template));
      const date = new Date(review.created_at);
      if (!Number.isNaN(date.getTime())) {
        const time = element('time', 'cg-review-card__date', date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }));
        time.dateTime = String(review.created_at).slice(0, 10);
        meta.append(time);
      }
      const quote = element('blockquote', 'cg-review-card__body');
      quote.append(element('p', '', review.body));
      const author = element('figcaption', 'cg-review-card__author');
      let name = review.reviewer_name || 'Customer';
      if (/^etsy (customer|buyer|user)$/i.test(name)) name = 'Customer';
      const avatar = element('span', 'cg-review-card__avatar', name.slice(0, 1).toUpperCase());
      avatar.setAttribute('aria-hidden', 'true');
      author.append(avatar, element('span', 'cg-review-card__name', name));
      if (review.verified_buyer) author.append(element('span', 'cg-review-card__verified', 'Verified buyer'));
      if (review.imported_from_etsy) author.append(element('span', 'cg-review-card__verified', 'Originally on Etsy'));
      figure.append(meta, quote, author);
      item.append(figure);
      list.append(item);
    }
    return list;
  }
  const correctedRatings = new Map();
  function updateSchema() {
    for (const script of document.querySelectorAll('script[type="application/ld+json"]')) {
      try {
        const data = JSON.parse(script.textContent);
        let changed = false;
        function visit(value) {
          if (!value || typeof value !== 'object') return;
          if (Array.isArray(value)) { value.forEach(visit); return; }
          if (value['@type'] === 'Product') {
            const url = String(value.url || value['@id'] || '');
            for (const [handle, rating] of correctedRatings) {
              if ((url.includes('/products/' + handle) || value.name === rating.title) && value.aggregateRating) {
                value.aggregateRating.reviewCount = rating.count;
                value.aggregateRating.ratingValue = rating.average;
                if (value.review) value.review = rating.reviews.filter(r => r.body && normalize(r.body) !== normalize(r.product_title)).slice(0, 10).map(r => ({
                  '@type': 'Review',
                  author: { '@type': 'Person', name: r.reviewer_name || 'Customer' },
                  reviewBody: r.body,
                  datePublished: String(r.created_at).slice(0, 10),
                  reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 }
                }));
                changed = true;
              }
            }
          }
          Object.values(value).forEach(visit);
        }
        visit(data);
        if (changed) {
          const updated = JSON.stringify(data);
          if (updated !== script.textContent) script.textContent = updated;
        }
      } catch (_) { /* Leave unrelated structured data intact. */ }
    }
  }
  async function sync(container) {
    if (container.dataset.cgReviewSync) return;
    container.dataset.cgReviewSync = 'loading';
    try {
      const id = container.dataset.cgReviewProductId;
      const [rules, raw] = await Promise.all([loadRules(container.dataset.cgReviewRules), loadReviews(id, container.dataset.cgReviewShop)]);
      const reviews = correctReviews(raw, id, rules);
      if (!reviews.length) return;
      const count = reviews.length;
      const average = (reviews.reduce((n, r) => n + Number(r.rating), 0) / count).toFixed(1);
      const summary = container.querySelector('.cg-review-summary');
      const template = summary.querySelector('.cg-stars');
      const title = container.dataset.cgReviewTitle;
      summary.querySelector('.cg-review-summary__average').firstChild.textContent = average;
      template.style.setProperty('--cg-stars-fill', Number(average) * 20 + '%');
      template.setAttribute('aria-label', average + ' out of 5 stars');
      const label = summary.querySelector('.cg-review-summary__count');
      const productLink = label.querySelector('a');
      label.replaceChildren(document.createTextNode('Based on ' + count + (count === 1 ? ' review' : ' reviews')));
      if (productLink) label.append(document.createTextNode(' of the '), productLink);
      const link = summary.querySelector('.cg-review-summary__all');
      if (link) link.firstChild.textContent = 'Read all ' + count + ' reviews ';
      const oldCards = container.querySelector('.cg-review-cards');
      const shown = cards(reviews, template, title);
      const limit = Number(container.dataset.cgReviewLimit) || 4;
      while (shown.children.length > limit) shown.lastElementChild.remove();
      if (oldCards) oldCards.replaceWith(shown); else container.append(shown);
      const section = container.closest('.cg-product-reviews');
      if (section.dataset.cgPageProductId === id) {
        const native = section.querySelector('.cg-product-reviews__widget');
        const toggle = section.querySelector('.cg-product-reviews__toggle span');
        if (native && toggle) {
          let complete = section.querySelector('.cg-review-all-list');
          if (!complete) { complete = element('div', 'cg-review-all-list'); native.after(complete); }
          complete.replaceChildren(cards(reviews, template, title));
          toggle.textContent = 'See all ' + count + ' reviews';
          native.hidden = true;
          function showReviewForm() {
            if (/^#(judgeme|jdgm|write-a-review)/i.test(location.hash)) native.hidden = false;
          }
          showReviewForm();
          window.addEventListener('hashchange', showReviewForm);
        }
        for (const badge of document.querySelectorAll('[data-cg-rating-product-id="' + id + '"]')) {
          badge.querySelector('.cg-product-rating__average').textContent = average;
          badge.querySelector('.cg-product-rating__count').textContent = count + (count === 1 ? ' review' : ' reviews');
          const badgeStars = badge.querySelector('.cg-stars');
          badgeStars.style.setProperty('--cg-stars-fill', Number(average) * 20 + '%');
          badgeStars.setAttribute('aria-label', average + ' out of 5 stars');
        }
        correctedRatings.set(container.dataset.cgReviewHandle, { count, average, reviews, title });
        updateSchema();
      }
      container.dataset.cgReviewSync = 'ready';
    } catch (error) {
      delete container.dataset.cgReviewSync;
      console.warn('Cargonaut review refresh unavailable; keeping the Shopify review display.', error);
    }
  }
  function scan() { document.querySelectorAll('[data-cg-review-product-id]').forEach(sync); }
  window.CgReviewSync = { scan };
  scan();
  document.addEventListener('shopify:section:load', scan);
  const observer = new MutationObserver(mutations => {
    if (mutations.some(m => [...m.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('script[type="application/ld+json"]') || n.querySelector?.('script[type="application/ld+json"]'))))) updateSchema();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
